import React from 'react';
import { GoogleOauthIcon, MicrosoftOauthIcon, ShopifyOauthIcon } from './OauthIcons';

export type OauthProvider = 'google' | 'microsoft' | 'shopify';
export type OauthAuthMode = 'signin' | 'signup';

const LABELS: Record<OauthProvider, Record<OauthAuthMode, string>> = {
    google: { signin: 'Sign in with G Suite', signup: 'Sign Up with G Suite' },
    microsoft: { signin: 'Sign in with Microsoft', signup: 'Sign Up with Microsoft' },
    shopify: { signin: 'Sign in with Shopify', signup: 'Sign up with Shopify' },
};

const GROUP_CLASS: Record<OauthProvider, string> = {
    google: 'd-grid google-btn-group in-iframe-hide',
    microsoft: 'd-grid microsoft-btn-group in-iframe-hide mt-2',
    shopify: 'd-grid shopify-btn-group in-iframe-hide mt-2',
};

const ICONS: Record<OauthProvider, React.FC> = {
    google: GoogleOauthIcon,
    microsoft: MicrosoftOauthIcon,
    shopify: ShopifyOauthIcon,
};

export interface OauthButtonProps {
    provider: OauthProvider;
    mode: OauthAuthMode;
    /** OAuth redirect URL. For Shopify, omit and the button opens the store-name modal. */
    href?: string;
    hidden?: boolean;
    /** Force window.location navigation (used on login Google/Microsoft). */
    forceNavigate?: boolean;
    className?: string;
    buttonClassName?: string;
}

/**
 * Shared OAuth CTA used on login and signup for G Suite, Microsoft, and Shopify.
 * Shopify opens #shopifyAuthModal; other providers navigate to `href`.
 */
const OauthButton: React.FC<OauthButtonProps> = ({
    provider,
    mode,
    href = '#',
    hidden = false,
    forceNavigate = false,
    className = '',
    buttonClassName = '',
}) => {
    const Icon = ICONS[provider];
    const isShopify = provider === 'shopify';
    const groupClass = `${GROUP_CLASS[provider]} ${hidden ? 'hide' : ''} ${className}`.trim();
    const btnClass = [
        'btn btn-light btn-lg login-btn google-btn',
        isShopify ? 'd-inline-flex align-items-center justify-content-center' : '',
        provider === 'google' ? 'google-icon-btn google-icon-text' : '',
        buttonClassName,
    ]
        .filter(Boolean)
        .join(' ');

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isShopify) return;
        if (forceNavigate && href && href !== '#') {
            e.preventDefault();
            window.location.href = href;
        }
    };

    return (
        <div className={groupClass}>
            <a
                href={href}
                className={btnClass}
                onClick={handleClick}
                {...(isShopify
                    ? { 'data-bs-toggle': 'modal', 'data-bs-target': '#shopifyAuthModal' }
                    : {})}
            >
                <span className={`svg-icon svg-icon-md me-2${isShopify ? ' d-inline-flex' : ''}`}>
                    <Icon />
                </span>
                {LABELS[provider][mode]}
            </a>
        </div>
    );
};

export default OauthButton;
